import { useEffect, useState } from "react";
import { Form, Row, Col, Card, Divider, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import CustomInputNumber from "../../../components/CustomInputNumber/InputNumber";
import FormFooter from "../../../components/CustomForms/FormFooter";
import { BTN_TXT, ERROR_MSGS, CREDIT_LIMIT_MSGS } from "../../../constants/constants";
import { getPlatformFeeData, enhanceBusEnt, getBusEnt } from "../../../redux/slices/creditLimitSlice";
import { formatFee, discountRateRules, maxFundRequiredPctRules, convertToPercentage, formatDiscountRatePercent, formatMaxFundRequiredPercent, percentToDiscountRate, percentToMaxFundRequiredPct } from "../../../helpers/helpers";
import { getDealCategoryDisplayName } from "../../../helpers/dealCategoryProducts";
import { count } from "../../../redux/slices/dealsSlice";
import { count as allCount } from "../../../redux/slices/AuthSlice";


const EnhanceEntBus = ({ data, onClose, status }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const { platformFeeData, enhanceSubmitLoading, error } = useSelector((state) => state.creditLimit);
    const [selectedPlatform, setSelectedPlatform] = useState(null);

    useEffect(() => {
        if (!platformFeeData || platformFeeData.length === 0) {
            dispatch(getPlatformFeeData());
        }
    }, [dispatch, platformFeeData]);

    useEffect(() => {
        if (data && platformFeeData) {
            form.setFieldsValue({
                dealCategory: getDealCategoryDisplayName(
                    data.dealCategoryId,
                    data.dealCategoryName,
                ),
                busName: data.orgName,
                entName: data.entName,
                currCreditLimit: data.approvedCredit,
                discountRate: formatDiscountRatePercent(data.annualDiscountRate),
                maxFundRequiredPct: formatMaxFundRequiredPercent(data.maxFundRequiredPct ?? 0.8),
                platformFee: data.platformFeeId,
            });
            setSelectedPlatform(
                platformFeeData.find((f) => f.platformFeeId === data.platformFeeId)
            );
        }
    }, [data, platformFeeData, form]);

    const handleSubmit = async (values) => {
        const payload = {
            busEntId: data.busEntId,
            currentCredit: data.approvedCredit,
            newCredit: values.enhancedCredit,
            discountRate: percentToDiscountRate(values.discountRate),
            maxFundRequiredPct: percentToMaxFundRequiredPct(values.maxFundRequiredPct),
        };
        const result = await dispatch(enhanceBusEnt(payload));
        const businessName = data?.orgName || "Business";
        const enterpriseName = data?.entName || "Enterprise";
        if (result?.meta?.requestStatus === "fulfilled" && result.payload?.statusCode === 201) {
            message.success(
                CREDIT_LIMIT_MSGS.ENHANCE_SUCCESS.replace("{business}", businessName).replace(
                    "{enterprise}",
                    enterpriseName,
                ),
            );
            dispatch(getBusEnt(status));
            dispatch(count("CREDITLIMIT"));
            dispatch(allCount("ALL"));
            onClose();
            return;
        }

        const errorMessage =
            (result?.payload &&
                (result.payload.message || result.payload.error || String(result.payload))) ||
            (result?.error &&
                (result.error.message || String(result.error))) ||
            CREDIT_LIMIT_MSGS.ENHANCE_FAILED.replace("{business}", businessName).replace(
                "{enterprise}",
                enterpriseName,
            );
        message.error(errorMessage);
    };

    return (
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
            <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Deal Category"
                        name="dealCategory"
                        disabled
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Business Name"
                        name="busName"
                        disabled
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Enterprise Name"
                        name="entName"
                        disabled
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInputNumber
                        label="Current Credit Limit"
                        name="currCreditLimit"
                        disabled
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInputNumber
                        label="Enhanced Credit"
                        name="enhancedCredit"
                        placeholder="Enter enhanced credit limit"
                        controls={false}
                        rules={[{ required: true, message: ERROR_MSGS.ENHANCE_CRED_ERR_MSG }]}
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomSelect
                        label="Platform Fee"
                        name="platformFee"
                        options={platformFeeData?.map((fee) => ({
                            label: convertToPercentage(fee.platformFee),
                            value: fee.platformFeeId,
                        }))}
                        onChange={(value) =>
                            setSelectedPlatform(
                                platformFeeData.find((f) => f.platformFeeId === value)
                            )
                        }
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Interest Rate (Annual)"
                        name="discountRate"
                        type="text"
                        placeholder="Enter Interest Rate (e.g. 7.5)"
                        rules={discountRateRules}
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Max Fund Required (%)"
                        name="maxFundRequiredPct"
                        type="text"
                        placeholder="Enter Max Fund Required % (60-80)"
                        rules={maxFundRequiredPctRules}
                    />
                </Col>

                {selectedPlatform && (
                    <Col xs={24}>
                        <Card
                            style={{
                                background: "#f5faff",
                                borderRadius: 6,
                                padding: "12px 16px",
                            }}
                        >
                            {[
                                { label: "Platform ID", value: selectedPlatform.platformFeeId },
                                { label: "Platform Fees", value: formatFee(selectedPlatform.platformFee, "percent") },
                                { label: "Stamp Fees", value: formatFee(selectedPlatform.stampFee, "amount") },
                                { label: "Miscellaneous Fees", value: formatFee(selectedPlatform.miscellaneousFees, "amount") },
                                { label: "Penal Charges", value: formatFee(selectedPlatform.penalCharge, "percent") },
                            ].map((item, index) => (
                                <div
                                    key={index}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr auto 1fr",
                                        alignItems: "center",
                                        marginBottom: "6px",
                                    }}
                                >
                                    <span style={{ fontWeight: 600 }}>{item.label}</span>
                                    <span style={{ textAlign: "center" }}>:</span>
                                    <span style={{ textAlign: "right" }}>{item.value}</span>
                                </div>
                            ))}
                        </Card>
                    </Col>
                )}

            </Row>

            <Divider />
            <FormFooter
                onCancelClick={onClose}
                submitLoading={enhanceSubmitLoading}
                btnName={BTN_TXT.SUBMIT}
                error={error}
            />
        </Form>
    );
};

export default EnhanceEntBus;
